/**
 * Safe Toast Utility - Prevents XSS by using textContent instead of innerHTML
 */

export const showSafeToast = (message, options = {}) => {
  const {
    duration = 2000,
    backgroundColor = 'rgba(50,50,50,0.95)',
    color = 'white',
    top = '60px'
  } = options;

  const toast = document.createElement('div');
  toast.style.cssText = `
    position: fixed;
    top: ${top};
    left: 50%;
    transform: translateX(-50%);
    background: ${backgroundColor};
    color: ${color};
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 500;
    z-index: 999999;
    transition: opacity 0.3s ease;
  `;
  toast.textContent = message;
  document.body.appendChild(toast);

  setTimeout(() => {
    toast.style.opacity = '0';
    setTimeout(() => toast.remove(), 300);
  }, duration);

  return toast;
};

export const showErrorToast = (message) => {
  return showSafeToast(sanitizeError(message), { backgroundColor: '#ef4444' });
};

export const showSuccessToast = (message) => {
  return showSafeToast(message, { backgroundColor: 'rgba(34, 197, 94, 0.95)' });
};

/**
 * Zwei-Level Fehlermeldungen:
 * Admin (info@snapshotpro.ai) → voller technischer Fehler
 * Normaler User → freundliche Meldung ohne technische Details
 */
const ADMIN_EMAIL = 'info@snapshotpro.ai';

const TECHNICAL_PATTERNS = [
  /api/i, /404/i, /500/i, /502/i, /422/i, /401/i, /403/i,
  /fal\.ai/i, /fal\.run/i, /fal\.media/i, /astria/i, /xai/i, /grok/i,
  /elevenlabs/i, /sync\.so/i, /pixverse/i, /openai/i, /anthropic/i,
  /gemini/i, /claude/i, /gpt-?4/i, /ltx/i, /nano.?banana/i,
  /model.*not found/i, /application.*not found/i,
  /endpoint/i, /proxy/i, /timeout/i, /RESOURCE_EXHAUSTED/i,
  /HTTPStatus/i, /status code/i, /response/i,
  /mongodb/i, /database/i, /collection/i,
  /traceback/i, /exception/i, /internal server/i,
  /\.py/i, /\.js/i, /line \d+/i,
  /https?:\/\//i, /request_id/i, /queue\.fal/i
];

const FRIENDLY_MESSAGES = {
  default: 'Das hat leider nicht geklappt. Bitte versuche es erneut.',
  generation: 'Die Bildgenerierung hat nicht geklappt. Bitte versuche es erneut.',
  video: 'Die Videogenerierung hat nicht geklappt. Bitte versuche es erneut.',
  credits: 'Nicht genug SNAPs. Bitte lade dein Guthaben auf.',
  network: 'Verbindungsproblem. Bitte prüfe deine Internetverbindung.',
};

function isAdmin() {
  try {
    const user = JSON.parse(localStorage.getItem('snapshot_user') || '{}');
    return user.email === ADMIN_EMAIL;
  } catch { return false; }
}

function isTechnical(msg) {
  if (!msg || typeof msg !== 'string') return false;
  return TECHNICAL_PATTERNS.some(p => p.test(msg));
}

function getFriendlyCategory(msg) {
  if (!msg) return 'default';
  const lower = msg.toLowerCase();
  if (lower.includes('snap') || lower.includes('credit') || lower.includes('guthaben')) return 'credits';
  if (lower.includes('video') || lower.includes('animation')) return 'video';
  if (lower.includes('generi') || lower.includes('bild')) return 'generation';
  if (lower.includes('netzwerk') || lower.includes('network') || lower.includes('verbindung')) return 'network';
  return 'default';
}

/**
 * Bereinigte Fehlermeldung: Admin bekommt alles, User bekommt freundliche Meldung.
 */
export function sanitizeError(message) {
  if (!message || typeof message !== 'string') return FRIENDLY_MESSAGES.default;
  if (isAdmin()) return message; // Admin sieht alles
  if (isTechnical(message)) {
    return FRIENDLY_MESSAGES[getFriendlyCategory(message)];
  }
  return message; // Nicht-technische Meldung durchlassen
}

export default showSafeToast;
